/**
 * O painel do dia: o que precisa de decisão agora.
 *
 * A ordem das coisas na tela é a ordem em que elas queimam: atrasado, vence
 * hoje, em risco, e só então a carga por setor. Um painel que abre com
 * gráficos bonitos e esconde a OS que venceu ontem é um painel que ninguém usa
 * duas vezes.
 */

import { formato } from '@pcp/dominio';
import type { LinhaCarteira } from '@pcp/dados';
import { cabecalhoDePagina, cartaoOs, h, metrica, render, sf, tituloDeSecao, vazio } from '@pcp/ui';
import type { Aplicacao } from '../estado.js';

export async function telaPainel(app: Aplicacao, destino: HTMLElement): Promise<void> {
  const p = await app.repo.painel();
  const abrir = (numero: number): void => app.roteador.ir(`/os/${numero}`);

  const nada = p.atrasadas.length + p.vencemHoje.length + p.emRisco.length === 0;

  render(destino, [
    h('.vstack.gap-4', [
      cabecalhoDePagina({
        olho: 'Produção',
        titulo: 'Painel do dia',
        lead: 'O que atrasou, o que vence hoje e o que está rodando agora.',
      }),

      h('.pcp-metricas', [
        metrica({
          valor: p.atrasadas.length,
          rotulo: 'Atrasadas',
          detalhe: p.atrasadas.length ? 'passaram do prazo prometido' : 'nenhuma',
          cor: p.atrasadas.length ? 'var(--pcp-atrasada)' : undefined,
          aoClicar: () => app.roteador.ir('/carteira'),
        }),
        metrica({
          valor: p.vencemHoje.length,
          rotulo: 'Vencem hoje',
          cor: p.vencemHoje.length ? 'var(--pcp-pausada)' : undefined,
        }),
        metrica({
          valor: p.emRisco.length,
          rotulo: 'Em risco',
          detalhe: 'o que falta não cabe no prazo',
          cor: p.emRisco.length ? 'var(--pcp-risco)' : undefined,
        }),
        metrica({
          valor: p.emProducao.length,
          rotulo: 'Em produção',
          cor: 'var(--pcp-producao)',
          aoClicar: () => app.roteador.ir('/quadro'),
        }),
        metrica({ valor: p.emFila.length, rotulo: 'Em fila' }),
        metrica({
          valor: p.prontas.length,
          rotulo: 'Prontas',
          detalhe: p.prontas.length ? 'esperando expedição' : undefined,
          cor: p.prontas.length ? 'var(--pcp-pronta)' : undefined,
        }),
        metrica({ valor: p.apontamentosDoDia, rotulo: 'Apontamentos hoje' }),
        p.alertasAbertos > 0
          ? metrica({
              valor: p.alertasAbertos,
              rotulo: 'Alertas abertos',
              cor: 'var(--sys-red)',
              aoClicar: () => app.roteador.ir('/alertas'),
            })
          : null,
        p.importacoesEmQuarentena > 0
          ? metrica({
              valor: p.importacoesEmQuarentena,
              rotulo: 'Em quarentena',
              detalhe: 'PDFs que não foram lidos',
              cor: 'var(--sys-orange)',
              aoClicar: () => app.roteador.ir('/importacoes'),
            })
          : null,
        p.etapasAClassificar > 0
          ? metrica({
              valor: p.etapasAClassificar,
              rotulo: 'A classificar',
              detalhe: 'equipamento sem estação',
              cor: 'var(--sys-purple)',
              aoClicar: () => app.roteador.ir('/triagem'),
            })
          : null,
      ]),

      nada
        ? h('.cartao', vazio(
            'checkmark-circle',
            'Nada queimando',
            'Nenhuma OS atrasada, vencendo hoje ou em risco. O que estiver em produção aparece no quadro.',
          ))
        : null,

      p.atrasadas.length > 0 ? grupo('Atrasadas', p.atrasadas, abrir, 'exclamationmark-triangle-fill') : null,
      p.vencemHoje.length > 0 ? grupo('Vencem hoje', p.vencemHoje, abrir, 'alarm') : null,
      p.emRisco.length > 0 ? grupo('Em risco de atrasar', p.emRisco, abrir, 'hourglass') : null,
      p.prontas.length > 0 ? grupo('Prontas, esperando saída', p.prontas, abrir, 'tray') : null,

      h('.cartao', [
        h('.bloco-t', 'Carga por setor'),
        h('.t-subhead.t-secondary', { style: { marginBottom: 'var(--sp-3)' } },
          'Soma do tempo previsto das etapas que ainda não terminaram.'),
        h(
          '.pcp-carga',
          (() => {
            const maior = Math.max(1, ...p.cargaPorSetor.map((c) => c.segundosEmFila));
            return p.cargaPorSetor.map((c) =>
              h('.pcp-carga-linha', { style: { '--pcp-cor': c.setor.cor } }, [
                h('.hstack.gap-2', [
                  h('span.t-callout', c.setor.nome),
                  c.emExecucao > 0
                    ? h('span.selo.s-azul', { title: 'em execução' }, String(c.emExecucao))
                    : null,
                ]),
                h('.pcp-carga-barra', h('i', { style: { width: `${(c.segundosEmFila / maior) * 100}%` } })),
                h('.pcp-carga-valor', { title: `${c.etapas} etapa(s)` }, formato.hhmm(c.segundosEmFila)),
              ]),
            );
          })(),
        ),
      ]),
    ]),
  ]);
}

function grupo(
  titulo: string,
  linhas: LinhaCarteira[],
  aoAbrir: (numero: number) => void,
  icone: string,
): HTMLElement {
  return h('.vstack.gap-2', [
    tituloDeSecao(`${titulo} · ${linhas.length}`),
    h(
      '.pcp-lista-os',
      linhas.slice(0, 12).map((l) => cartaoOs(l, { aoClicar: aoAbrir })),
    ),
    linhas.length > 12
      ? h('.t-subhead.t-secondary', { style: { paddingLeft: '4px' } }, [
          sf(icone, 'sf-xs'),
          ` e mais ${linhas.length - 12}`,
        ])
      : null,
  ]);
}
